import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Download, Plus, Star, Package, AlertTriangle, Layers, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { OVERLINE, H1, btnPrimary, btnSecondary, StatusBadge, useAdmin } from "@/components/admin/ui";

const money = (c) => (c == null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(c / 100));
const timeAgo = (iso) => {
  if (!iso) return null;
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};
const TABS = [["", "All"], ["draft", "Drafts"], ["published", "Published"], ["archived", "Archived"]];

export default function AdminCommerceProducts() {
  const { admin } = useAdmin();
  const canEdit = admin?.permissions?.includes("marketplace");
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [overview, setOverview] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [tab, setTab] = useState("");
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => {
    api.admCommerceProducts({ status: tab || undefined }).then(setRows).catch(() => setRows([]));
    api.admCommerceOverview().then(setOverview).catch(() => {});
    api.admCommerceSyncStatus().then(setSyncStatus).catch(() => {});
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const importPf = async () => {
    setBusy(true);
    try {
      await api.admCommerceSeedCats();
      const r = await api.admCommerceImport();
      const bits = [`imported ${r.imported || 0}`, `updated ${r.updated || 0}`];
      if (r.unpublished) bits.push(`hid ${r.unpublished}`);
      toast.success(`Printify sync complete — ${bits.join(", ")}`);
      load();
    } catch { toast.error("Sync failed"); } finally { setBusy(false); }
  };
  const syncPf = async () => {
    setSyncing(true);
    try {
      const r = await api.admCommerceSync();
      const bits = [`updated ${r.updated || 0} product(s)`];
      if (r.imported) bits.push(`imported ${r.imported}`);
      if (r.unpublished) bits.push(`hid ${r.unpublished} unavailable`);
      if (r.republished) bits.push(`restored ${r.republished}`);
      toast.success(`Refreshed from Printify — ${bits.join(", ")}`);
      load();
    } catch { toast.error("Printify sync failed"); } finally { setSyncing(false); }
  };
  const create = async () => {
    try { const p = await api.admCommerceCreate({ title: "New product" }); nav(`/admin/commerce/products/${p.id}`); }
    catch { toast.error("Failed"); }
  };
  const publish = async (p) => {
    try { await api.admCommercePublish(p.id); toast.success("Published"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
  };
  const archive = async (p) => { if (!window.confirm(`Archive "${p.title}"?`)) return; try { await api.admCommerceArchive(p.id); load(); } catch { toast.error("Failed"); } };

  return (
    <div className="p-6 md:p-8" data-testid="admin-commerce-products">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><span className={OVERLINE}>Commerce</span><h1 className={`${H1} mt-1`}>Products</h1></div>
        {canEdit && (
          <div className="flex gap-2 flex-wrap">
            <button onClick={syncPf} disabled={syncing} className={btnSecondary} data-testid="commerce-sync-btn" title="Pull latest prices & details from Printify">{syncing ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Sync from Printify</button>
            <button onClick={importPf} disabled={busy} className={btnSecondary} data-testid="commerce-import-btn">{busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Import from Printify</button>
            <button onClick={create} className={btnPrimary} data-testid="commerce-new-btn"><Plus size={15} /> New product</button>
          </div>
        )}
      </div>

      {overview && (
        <div className="grid grid-cols-3 gap-3 mt-5 max-w-lg">
          {[["Total", overview.total_products], ["Published", overview.published], ["Drafts", overview.drafts]].map(([l, v]) => (
            <div key={l} className="bg-white border border-[#E5E2DC] p-3"><p className="text-xs text-[#8A8478]">{l}</p><p className="text-xl font-semibold text-[#1F2A22]">{v}</p></div>
          ))}
        </div>
      )}

      {syncStatus?.configured && (
        <div className={`mt-4 border p-4 flex flex-wrap items-center gap-x-8 gap-y-2 ${syncStatus.status === "error" ? "border-red-300 bg-red-50" : "border-[#E5E2DC] bg-white"}`} data-testid="printify-sync-status">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-[#8A8478]">Printify sync</p>
            <p className={`text-sm font-semibold ${syncStatus.status === "error" ? "text-red-700" : "text-[#2E7D46]"}`} data-testid="printify-sync-state">
              {syncStatus.status === "error" ? "Last sync failed" : syncStatus.status === "ok" ? "Healthy" : "Never run"}
            </p>
          </div>
          {syncStatus.last_success_at && <div><p className="text-[11px] uppercase tracking-wide text-[#8A8478]">Last success</p><p className="text-sm text-[#1F2A22]" data-testid="printify-sync-last-success">{timeAgo(syncStatus.last_success_at)}</p></div>}
          {syncStatus.last_failure_at && <div><p className="text-[11px] uppercase tracking-wide text-[#8A8478]">Last failure</p><p className="text-sm text-red-700" data-testid="printify-sync-last-failure">{timeAgo(syncStatus.last_failure_at)}</p></div>}
          <div><p className="text-[11px] uppercase tracking-wide text-[#8A8478]">Variants</p><p className="text-sm text-[#1F2A22]" data-testid="printify-sync-variants">
              {syncStatus.current_variant_total ?? syncStatus.last_result?.variant_total ?? 0} synced{(syncStatus.current_variant_unavailable ?? syncStatus.last_result?.variant_unavailable) ? ` · ${syncStatus.current_variant_unavailable ?? syncStatus.last_result?.variant_unavailable} unavailable` : ""}
            </p></div>
          <div><p className="text-[11px] uppercase tracking-wide text-[#8A8478]">Auto-sync</p><p className="text-sm text-[#1F2A22]">{syncStatus.schedule}</p></div>
          {syncStatus.last_error && <p className="w-full text-xs text-red-700" data-testid="printify-sync-error">{syncStatus.last_error}</p>}
        </div>
      )}

      <div className="flex gap-1.5 mt-5 mb-4">
        {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`px-3 py-1.5 text-xs border ${tab === k ? "bg-[#1F2A22] text-white border-[#1F2A22]" : "border-[#E5E2DC] text-[#5C6B61]"}`} data-testid={`commerce-tab-${k || "all"}`}>{l}</button>)}
      </div>

      {rows === null ? (
        <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-[#FF6A00]" /></div>
      ) : rows.length === 0 ? (
        <div className="py-12 text-center text-[#5C6B61]"><Package size={34} className="mx-auto text-[#C7C2B8]" /><p className="mt-3">No products yet. Click <b>Import from Printify</b> to pull your published Printify products in as drafts.</p></div>
      ) : (
        <div className="border border-[#E5E2DC] bg-white overflow-x-auto" data-testid="commerce-list">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-[#8A8478] border-b border-[#E5E2DC]">
              <th className="px-4 py-2.5">Product</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Price</th><th className="px-4 py-2.5">Cost</th><th className="px-4 py-2.5">Images</th><th className="px-4 py-2.5">Printify</th><th className="px-4 py-2.5 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-b border-[#F0EEE9] last:border-0" data-testid={`commerce-row-${p.id}`}>
                  <td className="px-4 py-2.5">
                    <button onClick={() => nav(`/admin/commerce/products/${p.id}`)} className="flex items-center gap-3 text-left" data-testid={`commerce-edit-${p.id}`}>
                      {p.primary_image ? <img src={p.primary_image} alt="" className="w-10 h-10 object-cover border border-[#1F2A22]/10" /> : <div className="w-10 h-10 bg-[#F2F0EB] grid place-items-center text-[#C7C2B8]"><Package size={16} /></div>}
                      <span className="font-medium text-[#1F2A22] flex items-center gap-1.5">{p.title}{p.featured && <Star size={13} className="fill-[#FF6A00] text-[#FF6A00]" />}</span>
                    </button>
                  </td>
                  <td className="px-4 py-2.5"><StatusBadge status={p.status} /></td>
                  <td className="px-4 py-2.5 text-[#1F2A22]">
                    {money(p.sale_price || p.price)}
                    {p.price_out_of_sync && (
                      <span className="flex items-center gap-1 text-[#8A5A44] text-[11px] mt-0.5" data-testid={`commerce-price-drift-${p.id}`} title={`HomeRange price differs from the last Printify price (${money(p.printify_synced_price)})`}>
                        <AlertTriangle size={11} /> differs from Printify {money(p.printify_synced_price)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-[#8A8478]">{money(p.cost)}</td>
                  <td className="px-4 py-2.5">{p.image_count < 2 ? <span className="inline-flex items-center gap-1 text-[#8A5A44] text-xs"><AlertTriangle size={13} /> {p.image_count}</span> : <span className="text-[#5C6B61]">{p.image_count}</span>}</td>
                  <td className="px-4 py-2.5" data-testid={`commerce-sync-cell-${p.id}`}>
                    {!p.printify_product_id ? (
                      <span className="text-[#C7C2B8] text-xs">Not linked</span>
                    ) : p.last_printify_sync ? (
                      <span className="inline-flex items-center gap-1 text-[#5C6B61] text-xs" title={new Date(p.last_printify_sync).toLocaleString()}><RefreshCw size={11} className="text-[#2E7D46]" /> {timeAgo(p.last_printify_sync)}</span>
                    ) : (
                      <span className="text-[#8A5A44] text-xs">Never synced</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-2">
                      {p.status !== "published" && <button disabled={!canEdit} onClick={() => publish(p)} className="text-xs border border-[#2E7D46] text-[#2E7D46] px-2 py-1 disabled:opacity-40" data-testid={`commerce-publish-${p.id}`}>Publish</button>}
                      {p.status === "published" && <button disabled={!canEdit} onClick={() => api.admCommerceUnpublish(p.id).then(load)} className="text-xs border border-[#E5E2DC] text-[#5C6B61] px-2 py-1">Unpublish</button>}
                      {p.status !== "archived" && <button disabled={!canEdit} onClick={() => archive(p)} className="text-xs border border-[#E5E2DC] text-[#5C6B61] px-2 py-1">Archive</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <button onClick={() => nav("/admin/commerce/categories")} className="mt-5 inline-flex items-center gap-1.5 text-sm text-[#FF6A00] font-semibold" data-testid="commerce-categories-link"><Layers size={15} /> Manage categories & collections</button>
    </div>
  );
}
